import {Component, OnInit} from '@angular/core';
import {ActivatedRoute, Router} from '@angular/router';
import {Title} from '@angular/platform-browser';
import {HttpErrorResponse} from '@angular/common/http';
import {DataService} from '../../../../Services/data.service';
import {BsToastService} from '../../../../Services/bs-toast-service';
import {AppConfigService} from '../../../../Services/app-config.service';
import {StorageService} from '../../../../Services/storage.service';

@Component({
  selector: 'app-bank-import-wizard',
  templateUrl: './bank-import-wizard.component.html',
  styleUrls: ['./bank-import-wizard.component.css'],
  standalone: false
})
export class BankImportWizardComponent implements OnInit {

  private storedFile: File | null = null;

  vm: any = {
    step: 1,
    activeTab: 'import',
    banks: [],
    bank_id: '',
    company_id: '',
    selectedFile: null,
    fileName: '',
    uploading: false,
    confirming: false,
    importResult: null,
    csvMapping: null,
    detectedColumns: [],
    suggestedMapping: null,
    sampleRows: [],
    showIgnored: false,
    filterMode: 'all',
    sortCol: 'date',
    sortDir: 'asc',
    rowDecisions: {},
    createEntryRow: null,
    createEntryForm: {account_id: '', location_id: '', memo: ''},
    accounts: [],
    locations: [],
    dateFrom: '',
    dateTo: '',
    reRunningRules: false,
    groupSelection: {},
    groupEntryForm: {account_id: '', location_id: '', memo: ''},
    groupPosting: false,
  };

  private get apiBase(): string {
    return this.appConfig.apiBaseUrl;
  }

  constructor(
    private router: Router,
    private route: ActivatedRoute,
    private titleService: Title,
    private tableDataService: DataService,
    private notification: BsToastService,
    private appConfig: AppConfigService,
    private localStorage: StorageService,
  ) {
  }

  ngOnInit() {
    this.titleService.setTitle('Bank Transaction Import');
    // this.vm.company_id = this.localStorage.StorageGet('companyId') || '';
    this.loadBanks();
    this.loadAccounts();
    this.loadLocations();
    const qp = this.route.snapshot.queryParams;
    const importId = qp['import_id'];
    if (importId) {
      this.vm.bank_id = qp['bank_id'] || this.vm.bank_id;
      this.tableDataService.getData('bank-import', importId).subscribe({
        next: (detail: any) => {
          this.vm.importResult = detail;
          this.vm.bank_id = this.vm.bank_id || detail.bank_id || '';
          this.vm.company_id = this.vm.company_id || detail.company_id || '';
          this.initDecisions(detail.items || []);
          this.vm.step = 3;
        },
        error: () => {
          this.notification.showErrorToast('Failed to load import session.');
        }
      });
    }
  }

  loadBanks() {
    this.tableDataService.getApiListData('bank', '?state=active&pageNumber=0&pageSize=999999').subscribe({
      next: (result: any) => {
        this.vm.banks = result.Data || [];
      },
      error: () => {
      }
    });
  }

  loadAccounts() {
    this.tableDataService.getApiListData('account', '?pageNumber=0&pageSize=999999').subscribe({
      next: (result: any) => {
        this.vm.accounts = result.Data || [];
      },
      error: () => {
      }
    });
  }

  loadLocations() {
    this.tableDataService.getApiListData('location', '?pageNumber=0&pageSize=999999').subscribe({
      next: (result: any) => {
        this.vm.locations = result.Data || [];
      },
      error: () => {
      }
    });
  }

  onBankChange(bankId: string) {
    const bank = this.vm.banks.find((b: any) => b.id === bankId);
    if (bank) {
      this.vm.company_id = bank.company_id || this.vm.company_id;
    }
  }

  onFileSelected(event: Event) {
    const input = event.target as HTMLInputElement;
    if (input.files && input.files.length > 0) {
      this.storedFile = input.files[0];
      this.vm.selectedFile = this.storedFile;
      this.vm.fileName = input.files[0].name;
    }
  }

  get canUpload(): boolean {
    return !!this.vm.bank_id && !!this.vm.selectedFile;
  }

  upload() {
    if (!this.canUpload) {
      return;
    }
    this.vm.uploading = true;
    const formData = new FormData();
    formData.append('file', this.vm.selectedFile);
    formData.append('bank_id', this.vm.bank_id);
    formData.append('company_id', this.vm.company_id);
    if (this.vm.dateFrom) {
      formData.append('date_from', this.vm.dateFrom);
    }
    if (this.vm.dateTo) {
      formData.append('date_to', this.vm.dateTo);
    }

    console.log('[BankImport][upload] payload:', {
      file: this.vm.selectedFile?.name,
      bank_id: this.vm.bank_id,
      company_id: this.vm.company_id,
      date_from: this.vm.dateFrom || null,
      date_to: this.vm.dateTo || null,
      url: this.apiBase + 'bank-import/upload'
    });
    this.tableDataService.postFormData('bank-import/upload', formData).subscribe({
      next: (result: any) => {
        this.vm.uploading = false;
        if (result.status === 'mapping_required') {
          console.log('[BankImport][upload] mapping_required result:', result);
          this.vm.detectedColumns = result.detected_columns || [];
          this.vm.suggestedMapping = {...result.suggested_mapping};
          this.vm.csvMapping = {...result.suggested_mapping};
          this.vm.sampleRows = result.sample_rows || [];
          this.vm.importResult = result;
          this.vm.step = 2;
        } else {
          console.log('[BankImport][upload] result:', result);
          this.vm.importResult = result;
          this.initDecisions(result.items || []);
          this.vm.step = 3;
        }
      },
      error: (err: HttpErrorResponse) => {
        this.vm.uploading = false;
        const msg = err.error?.message || 'Upload failed. Please check the file and try again.';
        this.notification.showErrorToast(msg);
      }
    });
  }

  confirmMapping() {
    const importId = this.vm.importResult?.import_id || this.vm.importResult?.id || this.vm.importResult?.importId;
    console.log('[BankImport][confirmMapping] importResult:', this.vm.importResult, 'importId:', importId);
    this.vm.uploading = true;
    const formData = new FormData();
    formData.append('file', this.storedFile || this.vm.selectedFile);
    formData.append('bank_id', this.vm.bank_id);
    formData.append('company_id', this.vm.company_id);
    formData.append('import_id', importId);
    formData.append('csv_mapping', JSON.stringify(this.vm.csvMapping));
    if (this.vm.dateFrom) {
      formData.append('date_from', this.vm.dateFrom);
    }
    if (this.vm.dateTo) {
      formData.append('date_to', this.vm.dateTo);
    }

    console.log('[BankImport][confirmMapping] posting, file:', (this.storedFile || this.vm.selectedFile)?.name, 'import_id:', importId);
    this.tableDataService.postFormData('bank-import/upload', formData).subscribe({
      next: (result: any) => {
        console.log('[BankImport][confirmMapping] result:', result);
        this.vm.uploading = false;
        this.vm.importResult = result;
        this.initDecisions(result.items || []);
        this.vm.step = 3;
      },
      error: (err: HttpErrorResponse) => {
        console.error('[BankImport][confirmMapping] error:', err);
        this.vm.uploading = false;
        const msg = err.error?.message || 'Mapping confirmation failed.';
        this.notification.showErrorToast(msg);
      }
    });
  }

  initDecisions(items: any[]) {
    this.vm.rowDecisions = {};
    for (const item of items) {
      if (item.status === 'matched' && item.match_confidence === 'high') {
        this.vm.rowDecisions[item.bank_transaction_id] = 'accept_match';
      }
    }
  }

  get filteredItems(): any[] {
    const items: any[] = this.vm.importResult?.items || [];
    const showIgnored = this.vm.showIgnored;
    const filterMode = this.vm.filterMode;
    const filtered = items.filter(i => {
      if (i.status === 'ignored' && !showIgnored) {
        return false;
      }
      if (filterMode === 'unmatched' && i.status !== 'unmatched' && !(i.status === 'ignored' && showIgnored)) {
        return false;
      }
      return true;
    });
    const col = this.vm.sortCol;
    const dir = this.vm.sortDir === 'asc' ? 1 : -1;
    return filtered.sort((a, b) => {
      let av = a[col] ?? '';
      let bv = b[col] ?? '';
      if (col === 'amount') {
        av = Number(av);
        bv = Number(bv);
      }
      if (av < bv) {
        return -1 * dir;
      }
      if (av > bv) {
        return 1 * dir;
      }
      return 0;
    });
  }

  sortBy(col: string) {
    if (this.vm.sortCol === col) {
      this.vm.sortDir = this.vm.sortDir === 'asc' ? 'desc' : 'asc';
    } else {
      this.vm.sortCol = col;
      this.vm.sortDir = 'asc';
    }
  }

  get allUnmatchedActioned(): boolean {
    const items: any[] = this.vm.importResult?.items || [];
    const unmatched = items.filter(i => i.status === 'unmatched');
    if (unmatched.length === 0) {
      return true;
    }
    return unmatched.every(i => !!this.vm.rowDecisions[i.bank_transaction_id]);
  }

  setDecision(item: any, action: string) {
    this.vm.rowDecisions[item.bank_transaction_id] = action;
    if (action === 'create_entry') {
      this.vm.createEntryRow = item;
      this.vm.createEntryForm = {account_id: '', location_id: '', memo: item.description || ''};
    } else {
      if (this.vm.createEntryRow?.bank_transaction_id === item.bank_transaction_id) {
        this.vm.createEntryRow = null;
      }
    }
  }

  submitCreateEntry(item: any) {
    const importId = this.vm.importResult?.import_id;
    if (!importId) {
      return;
    }
    const bank = this.vm.banks.find((b: any) => b.id === this.vm.bank_id);
    const cashAccountId = bank?.gl_account_id || bank?.cash_account_id || null;
    const payload = {
      bank_import_transaction_id: item.bank_import_transaction_id || item.id,
      account_id: this.vm.createEntryForm.account_id,
      cash_account_id: cashAccountId,
      location_id: this.vm.createEntryForm.location_id || null,
      memo: this.vm.createEntryForm.memo || null
    };
    console.log('[BankImport][createEntry] payload:', payload);
    this.tableDataService.post(`bank-import/${importId}/create-entry`, payload).subscribe({
      next: () => {
        this.notification.showSuccessToast('Transaction entry created.');
        this.vm.rowDecisions[item.bank_transaction_id] = 'created';
        this.vm.createEntryRow = null;
        item.status = 'posted';
      },
      error: (err: HttpErrorResponse) => {
        this.notification.showErrorToast(err.error?.message || 'Failed to create entry.');
      }
    });
  }

  // ── Group (bulk) create-entry: lets the user post many like-kind unmatched
  // transactions (e.g. 50 misc office expense lines) to a single offsetting
  // account in one step, instead of repeating the Create Entry form per row.
  // Dates/amounts/descriptions for each row are preserved; only the offsetting
  // account/location (and optionally a shared memo) are applied in bulk. ──

  toggleGroupSelection(item: any) {
    const key = item.bank_transaction_id;
    if (this.vm.groupSelection[key]) {
      delete this.vm.groupSelection[key];
    } else {
      this.vm.groupSelection[key] = item;
      // Selecting rows for bulk grouping supersedes the single-row create-entry form.
      this.vm.createEntryRow = null;
    }
  }

  get selectedUnmatchedCount(): number {
    return Object.keys(this.vm.groupSelection || {}).length;
  }

  clearGroupSelection() {
    this.vm.groupSelection = {};
    this.vm.groupEntryForm = {account_id: '', location_id: '', memo: ''};
  }

  submitGroupEntries() {
    const importId = this.vm.importResult?.import_id;
    const items: any[] = Object.values(this.vm.groupSelection || {});
    if (!importId || items.length === 0 || !this.vm.groupEntryForm.account_id) {
      return;
    }
    const bank = this.vm.banks.find((b: any) => b.id === this.vm.bank_id);
    const cashAccountId = bank?.gl_account_id || bank?.cash_account_id || null;
    this.vm.groupPosting = true;

    const postNext = (index: number, successCount: number, failCount: number) => {
      if (index >= items.length) {
        this.vm.groupPosting = false;
        if (successCount > 0) {
          this.notification.showSuccessToast(`${successCount} transaction(s) posted to the selected account.`);
        }
        if (failCount > 0) {
          this.notification.showErrorToast(`${failCount} transaction(s) failed to post.`);
        }
        this.clearGroupSelection();
        return;
      }
      const item = items[index];
      const payload = {
        bank_import_transaction_id: item.bank_import_transaction_id || item.id,
        account_id: this.vm.groupEntryForm.account_id,
        cash_account_id: cashAccountId,
        location_id: this.vm.groupEntryForm.location_id || null,
        memo: this.vm.groupEntryForm.memo || item.description || null
      };
      console.log('[BankImport][submitGroupEntries] payload:', payload);
      this.tableDataService.post(`bank-import/${importId}/create-entry`, payload).subscribe({
        next: () => {
          this.vm.rowDecisions[item.bank_transaction_id] = 'created';
          item.status = 'posted';
          postNext(index + 1, successCount + 1, failCount);
        },
        error: () => {
          postNext(index + 1, successCount, failCount + 1);
        }
      });
    };

    postNext(0, 0, 0);
  }

  alwaysIgnore(item: any) {
    const bankId = this.vm.bank_id || this.vm.importResult?.bank_id || '';
    const companyId = this.vm.company_id || this.vm.importResult?.company_id || '';
    const payload = {
      bank_id: bankId,
      company_id: companyId,
      field: 'description',
      operator_type: 'contains',
      value: item.description,
      case_sensitive: false,
      note: 'Auto-created from import review'
    };
    console.log('[BankImport][alwaysIgnore] payload:', payload);
    item.status = 'ignored';
    this.vm.rowDecisions[item.bank_transaction_id] = 'ignored';
    this.tableDataService.post('bank-ignore-rules', payload).subscribe({
      next: () => {
        this.notification.showSuccessToast('Ignore rule created.');
        this.reRunRules();
      },
      error: () => {
        item.status = 'unmatched';
        delete this.vm.rowDecisions[item.bank_transaction_id];
        this.notification.showErrorToast('Failed to create ignore rule.');
      }
    });
  }

  reRunRules() {
    const importId = this.vm.importResult?.import_id;
    if (!importId) {
      return;
    }
    this.vm.reRunningRules = true;
    this.tableDataService.post(`bank-import/${importId}/re-run-rules`, {}).subscribe({
      next: (result: any) => {
        this.vm.reRunningRules = false;
        const newlyIgnored = result.newly_ignored || 0;
        if (newlyIgnored > 0) {
          this.notification.showSuccessToast(`${newlyIgnored} row(s) newly ignored by updated rules.`);
        }
        // Refresh the full item list from the session detail
        this.tableDataService.getData('bank-import', importId).subscribe({
          next: (detail: any) => {
            this.vm.importResult = detail;
            this.vm.bank_id = detail.bank_id || this.vm.bank_id;
            this.vm.company_id = detail.company_id || this.vm.company_id;
            this.initDecisions(detail.items || []);
          },
          error: () => {
          }
        });
      },
      error: () => {
        this.vm.reRunningRules = false;
        this.notification.showErrorToast('Failed to re-apply ignore rules.');
      }
    });
  }

  confirmImport() {
    const importId = this.vm.importResult?.import_id;
    if (!importId) {
      return;
    }
    this.vm.confirming = true;
    const rowDecisions = Object.entries(this.vm.rowDecisions)
      .filter(([, action]) => action !== 'created' && action !== 'ignored')
      .map(([id, action]) => ({bank_import_transaction_id: id, action}));

    this.tableDataService.post(`bank-import/${importId}/confirm`, {import_id: importId, row_decisions: rowDecisions}).subscribe({
      next: () => {
        this.vm.confirming = false;
        this.vm.step = 4;
        this.notification.showSuccessToast('Import confirmed successfully.');
      },
      error: (err: HttpErrorResponse) => {
        this.vm.confirming = false;
        this.notification.showErrorToast(err.error?.message || 'Confirm failed.');
      }
    });
  }

  startOver() {
    this.vm.step = 1;
    this.vm.selectedFile = null;
    this.vm.fileName = '';
    this.vm.importResult = null;
    this.vm.csvMapping = null;
    this.vm.rowDecisions = {};
    this.vm.createEntryRow = null;
  }

  goToHistory() {
    this.router.navigate(['/transactions/bank-import/history'], {state: {bank_id: this.vm.bank_id, company_id: this.vm.company_id}}).then();
  }

  statusBadgeClass(status: string): string {
    switch (status) {
      case 'matched':
        return 'fpt-badge-success';
      case 'unmatched':
        return 'fpt-badge-warning';
      case 'duplicate':
        return 'badge bg-secondary';
      case 'ignored':
        return 'badge bg-light text-muted';
      case 'posted':
        return 'fpt-badge-success';
      case 'skipped':
        return 'badge bg-secondary';
      default:
        return 'badge bg-light';
    }
  }

  amountClass(amount: number): string {
    return amount < 0 ? 'text-danger' : 'text-success';
  }
}
